import React, { useState, ChangeEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FormControl,
  TextInput,
  Dropdown,
  InputGroup,
  Loader,
} from 'components'

import {
  formatPhoneNumberOnBlur,
  getPhoneNumberMaxLength,
  validateKeyPress,
  trimFirstSpace,
  handleDisableOnPaste,
  thaiCountryCodeOption,
} from 'helpers/common'
import type { BranchForForm } from 'types'

import { findSelected } from '../domain'

const BranchContactSection: React.FC = () => {
  const [contactNameChecking, setContactNameChecking] = useState<boolean>(false)

  const { control, errors, setValue, trigger, getValues } =
    useFormContext<BranchForForm>()
  const { t } = useTranslation(['branch', 'common'])

  const base = 'f-branch__form'

  return (
    <div className={`${base}__section`}>
      <span className={`${base}__title`}>
        {t('branch:contactInformation.label')}
      </span>

      <div className={`${base}__info`}>
        <FormControl
          className={`${base}__item`}
          label={t('common:contactName.label')}
          required
        >
          <Controller
            type='text'
            name='contactName'
            control={control}
            render={(field) => (
              <TextInput
                value={field.value}
                onChange={(e) => {
                  field.onChange(trimFirstSpace(e))
                }}
                onBlur={async () => {
                  setContactNameChecking(true)
                  setValue('contactName', getValues('contactName').trim())
                  await trigger('contactName')
                  setContactNameChecking(false)
                }}
                placeholder={t('branch:insertContactName.placeholder')}
                isError={Boolean(errors.contactName?.message)}
                message={errors.contactName?.message}
                withIcon={contactNameChecking && <Loader />}
                maxLength={50}
                ref={field.ref}
              />
            )}
          />
        </FormControl>

        <FormControl
          className={`${base}__item`}
          label={t('common:phone.label')}
          required
        >
          <InputGroup
            variants='ternary'
            error={
              errors.contactCountryCode?.message ||
              errors.contactPhoneNumber?.message
            }
          >
            <Controller
              type='text'
              name='contactCountryCode'
              control={control}
              render={(field) => (
                <Dropdown
                  className='dropdown--telephone'
                  group='right'
                  options={[thaiCountryCodeOption]}
                  selected={findSelected([thaiCountryCodeOption], field.value)}
                  onChange={(value) =>
                    setValue('contactCountryCode', value.key)
                  }
                  isError={
                    Boolean(errors.contactPhoneNumber?.message) ||
                    Boolean(errors.contactCountryCode?.message)
                  }
                />
              )}
            />
            <Controller
              name='contactPhoneNumber'
              control={control}
              render={(field) => (
                <TextInput
                  ref={field.ref}
                  className={`${base}__phone-field`}
                  placeholder={t('common:phone.placeholder')}
                  type='text'
                  group='left'
                  isError={
                    Boolean(errors.contactCountryCode?.message) ||
                    Boolean(errors.contactPhoneNumber?.message)
                  }
                  value={field.value}
                  maxLength={getPhoneNumberMaxLength(field.value)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e.target.value)
                  }}
                  onFocus={() =>
                    setValue('contactPhoneNumber', field.value.replace('-', ''))
                  }
                  onBlur={formatPhoneNumberOnBlur((value) => {
                    setValue('contactPhoneNumber', value)
                    trigger('contactPhoneNumber')
                  })}
                  onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                  onPaste={(e) => handleDisableOnPaste(e)}
                />
              )}
            />
          </InputGroup>
        </FormControl>
      </div>
    </div>
  )
}

export default BranchContactSection
